import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useExperiment } from '../../context/ExperimentContext';
import { db } from '../../firebase';
import { collection, query, where, getDocs, doc, updateDoc, increment, getDoc } from 'firebase/firestore';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { QuestionnaireModal } from '../Shared/QuestionnaireModal';
import { HelperProfileModal } from '../Shared/HelperProfileModal';
import { notifyOfferAccepted, notifySessionComplete } from '../../utils/sendNotification';

export const MyRequests = () => {
  const { user, profile } = useAuth();
  const { CURRENT_PHASE, isPointsEnabled } = useExperiment();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState([]);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [tutorContacts, setTutorContacts] = useState({});
  const [showHelperProfile, setShowHelperProfile] = useState(false);
  const [viewingHelperId, setViewingHelperId] = useState(null);

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async () => {
    try {
      const q = query(collection(db, 'requests'), where('requesterId', '==', user.uid));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRequests(data);
      
      // Fetch details for ALL requests that have a helper attached (Pending AND Matched)
      const requestsWithHelper = data.filter(r => r.helperId);
      const contactPromises = requestsWithHelper.map(async (req) => {
        const tutorSnap = await getDoc(doc(db, 'users', req.helperId));
        if (tutorSnap.exists()) {
          const tutorData = tutorSnap.data();
          const whatsapp = tutorData.contactWhatsApp || tutorData.whatsapp || tutorData.phone || tutorData.phoneNumber || tutorData.mobile || '';
          setTutorContacts(prev => ({
            ...prev,
            [req.id]: { 
              email: tutorData.email, 
              whatsapp, 
              displayName: tutorData.displayName || 'Tutor' // ✅ Store Name here
            }
          }));
        }
      });
      await Promise.all(contactPromises);
    } catch (err) { console.error('Error:', err); } finally { setLoading(false); }
  };

  const handleAccept = async (request) => {
    if (window.confirm(`Accept help?`)) {
      try {
        await updateDoc(doc(db, 'requests', request.id), { status: 'matched', acceptedAt: new Date() });
        const helperSnap = await getDoc(doc(db, 'users', request.helperId));
        const helperData = helperSnap.data();
        if (helperData?.email) await notifyOfferAccepted(helperData.email, helperData.displayName || 'User', profile.displayName, request.title);
        alert('Help accepted!');
        fetchRequests();
      } catch (err) { alert('Error: ' + err.message); }
    }
  };

  const handleDecline = async (request) => {
    if (window.confirm('Decline this offer?')) {
      try {
        await updateDoc(doc(db, 'requests', request.id), { status: 'open', helperId: null });
        alert('Offer declined.');
        fetchRequests();
      } catch (err) { alert('Error: ' + err.message); }
    }
  };

  const openRatingModal = (request) => {
    setCurrentRequest(request);
    setSelectedRating(0);
    setComment('');
    setFiles([]);
    setShowRatingModal(true);
  };

  const openHelperProfile = (helperId) => {
    setViewingHelperId(helperId);
    setShowHelperProfile(true);
  };

  const handleFileChange = (e) => { setFiles([...files, ...Array.from(e.target.files)]); };
  const removeFile = (index) => { setFiles(files.filter((_, i) => i !== index)); };

  const submitCompletion = async () => {
    if (selectedRating === 0) { alert('Please select a rating!'); return; }
    try {
      let latencySeconds = 0;
      if (currentRequest.createdAt && currentRequest.acceptedAt) {
        const createdSec = currentRequest.createdAt.seconds || (currentRequest.createdAt / 1000);
        const acceptedSec = currentRequest.acceptedAt.seconds || (currentRequest.acceptedAt / 1000);
        latencySeconds = Math.max(0, acceptedSec - createdSec);
      }
      await updateDoc(doc(db, 'requests', currentRequest.id), {
        status: 'completed', completedAt: new Date(), rating: selectedRating,
        reviewComment: comment, latencySeconds, phase: CURRENT_PHASE, feedbackSubmitted: false
      });
      if (isPointsEnabled) await updateDoc(doc(db, 'users', currentRequest.helperId), { points: increment(1), ratingCount: increment(1) });
      alert('Session completed!');
      setShowRatingModal(false);
      setShowQuestionnaire(true); 
      fetchRequests();
    } catch (err) { console.error(err); alert('Error: ' + err.message); }
  };

  const getStatusColor = (status) => {
    if (status === 'open') return '#2d9e8f';
    if (status === 'pending') return '#f59e0b';
    if (status === 'matched') return '#3b82f6';
    if (status === 'completed') return '#10b981';
    return '#6b7280';
  };

  const displayRequests = showCompleted ? requests : requests.filter(req => req.status !== 'completed');

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>My Requests</h2>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem', color: '#666' }}>
            <input type="checkbox" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} style={{ accentColor: '#800000' }} /> Show Completed
          </label>
          <Button onClick={() => navigate('/post-request')} className="btn-primary">+ New Request</Button>
        </div>
      </div>

      {loading ? <p>Loading...</p> : displayRequests.length === 0 ? (
        <Card className="empty-state"><p>{showCompleted ? 'No requests yet.' : 'No active requests.'}</p><Button onClick={() => navigate('/post-request')} className="btn-primary">Post Request</Button></Card>
      ) : (
        <div className="requests-list">
          {displayRequests.map(request => {
            const tutorContact = tutorContacts[request.id];
            return (
              <Card key={request.id} className="request-card">
                <div className="request-header">
                  <span className="status-badge" style={{background: getStatusColor(request.status)}}>{request.status.toUpperCase()}</span>
                  <span className="request-subject">{request.subject}</span>
                </div>
                <h3>{request.title}</h3>
                <p>{request.description}</p>
                
                {/* PENDING STATE */}
                {request.status === 'pending' && (
                  <div className="offer-info">
                    {/* ✅ FIXED: Show Name if loaded, otherwise fallback to ID */}
                    <p><strong>Offer from:</strong> {tutorContact?.displayName || request.helperId?.substring(0, 8)}</p>
                    
                    <button onClick={() => openHelperProfile(request.helperId)} style={{ background: 'none', border: 'none', color: '#800000', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', marginBottom: '1rem' }}>
                       View Profile
                    </button>
                    <div className="action-buttons">
                      <Button onClick={() => handleAccept(request)} className="btn-success">✅ Accept</Button>
                      <Button onClick={() => handleDecline(request)} className="btn-outline"> Decline</Button>
                    </div>
                  </div>
                )}
                
                {/* MATCHED STATE */}
                {request.status === 'matched' && tutorContact && (
                  <div className="matched-info" style={{ background: '#f0fdf4', border: '2px solid #16a34a', borderRadius: '8px', padding: '1rem' }}>
                    <h4 style={{ margin: '0 0 1rem 0', color: '#166534' }}>✅ Matched! Contact Your Tutor:</h4>
                    <p><strong>Name:</strong> {tutorContact.displayName}</p>
                    <p><strong>Email:</strong> {tutorContact.email}</p>
                    {tutorContact.whatsapp && <p><strong>WhatsApp:</strong> {tutorContact.whatsapp} <a href={`https://wa.me/${tutorContact.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '0.5rem', color: '#25D366', textDecoration: 'none', fontWeight: 500 }}>💬 Chat</a></p>}
                    {!tutorContact.whatsapp && <p style={{ color: '#999', fontSize: '0.85rem', fontStyle: 'italic' }}> Tutor hasn't added WhatsApp number</p>}
                    <button onClick={() => openHelperProfile(request.helperId)} style={{ background: 'none', border: 'none', color: '#800000', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', marginBottom: '1rem' }}>
                       View Profile
                    </button>
                    <div className="action-buttons" style={{ marginTop: '1rem' }}>
                      <Button onClick={() => navigate(`/report/${request.helperId}/${request.id}`)} className="btn-danger-outline">⚠️ Report</Button>
                      <Button className="btn-teal" onClick={() => openRatingModal(request)}>✓ Mark Complete</Button>
                    </div>
                  </div>
                )}
                
                {request.status === 'completed' && <div className="completed-info"><p>✅ Session completed | Rating: {request.rating} stars</p>{request.reviewComment && <p>Comment: {request.reviewComment}</p>}</div>}
                {request.status === 'open' && <p className="waiting-msg">Waiting for tutors...</p>}
              </Card>
            );
          })}
        </div>
      )}
      
      {showRatingModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowRatingModal(false); }} style={{ zIndex: 1000 }}>
          <Card className="rating-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%', padding: '2rem', position: 'relative', background: 'white', borderRadius: '12px' }}>
            <h3 style={{ marginBottom: '0.5rem', color: '#800000' }}>Review Session</h3>
            <p style={{ marginBottom: '1.5rem', color: '#666' }}>Rate Tutor {currentRequest?.helperId?.substring(0, 8)}...</p>
            <div className="star-rating" style={{ marginBottom: '1.5rem' }}>
              {[1,2,3,4,5].map(star => (<button key={star} className={star <= selectedRating ? 'star filled' : 'star'} onClick={() => setSelectedRating(star)} type="button" style={{ background: 'none', border: 'none', fontSize: '2.5rem', color: star <= selectedRating ? '#f59e0b' : '#ddd', cursor: 'pointer' }}>★</button>))}
            </div>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Comment (Optional)</label>
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Great tutor!" rows="3" style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', fontFamily: 'inherit' }} />
            </div>
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Attach File (Optional)</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', border: '2px dashed #ddd', borderRadius: '8px', cursor: 'pointer', background: '#f9fafb' }}>
                <span>📎 Upload</span>
                <input type="file" multiple onChange={handleFileChange} style={{ display: 'none' }} />
              </label>
              {files.length > 0 && <div style={{ marginTop: '8px' }}>{files.map((f, i) => (<div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px', background: '#f3f4f6', borderRadius: '4px', marginBottom: '4px', fontSize: '13px' }}><span>📎 {f.name}</span><button type="button" onClick={() => removeFile(i)} style={{ color: 'red', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '0 4px' }}>✕</button></div>))}</div>}
            </div>
            <div className="btn-group" style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <Button onClick={() => setShowRatingModal(false)} className="btn-outline" style={{ padding: '0.75rem 1.5rem' }}>Cancel</Button>
              <Button onClick={submitCompletion} className="btn-primary" style={{ padding: '0.75rem 1.5rem' }}>Submit</Button>
            </div>
          </Card>
        </div>
      )}
      {showQuestionnaire && <QuestionnaireModal isOpen={showQuestionnaire} requestId={currentRequest?.id} userRole="requester" onClose={() => setShowQuestionnaire(false)} />}
      {showHelperProfile && <HelperProfileModal isOpen={showHelperProfile} onClose={() => setShowHelperProfile(false)} helperId={viewingHelperId} />}
    </div>
  );
};
